// pages/ReportView.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Play,
  Download,
  Edit,
  Copy,
  Trash2,
  RefreshCw,
  Calendar,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  BarChart3,
  TableIcon,
} from "lucide-react";
import { useReports } from "../../contexts/ReportContext";
import ReportChart from "../../components/ReportChart";
import ReportTable from "../../components/ReportTable";

const ReportView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    currentReport,
    reportResults,
    fetchReport,
    runReport,
    exportReport,
    deleteReport,
    cloneReport,
  } = useReports();

  const [running, setRunning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [runtimeFilters, setRuntimeFilters] = useState({});
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'chart'

  useEffect(() => {
    if (id) {
      loadReport();
    }
  }, [id]);

  const loadReport = async () => {
    try {
      await fetchReport(id);
    } catch (error) {
      console.error("Error loading report:", error);
    }
  };

  const handleRun = async () => {
    try {
      setRunning(true);
      await runReport(id, runtimeFilters);
    } catch (error) {
      alert("Failed to run report: " + error.message);
    } finally {
      setRunning(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const blob = await exportReport(id, format, runtimeFilters);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${currentReport?.name || "report"}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert("Failed to export report: " + error.message);
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        `Are you sure you want to delete "${currentReport?.name}"?`,
      )
    ) {
      try {
        await deleteReport(id);
        navigate("/reports");
      } catch (error) {
        alert("Failed to delete report");
      }
    }
  };

  const handleClone = async () => {
    const name = prompt(
      "Enter name for cloned report:",
      `${currentReport?.name} (Copy)`,
    );
    if (name) {
      try {
        const newReport = await cloneReport(id, name);
        navigate(`/reports/${newReport.id}`);
      } catch (error) {
        alert("Failed to clone report");
      }
    }
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  const updateRuntimeFilter = (field, value) => {
    setRuntimeFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearRuntimeFilters = () => {
    setRuntimeFilters({});
  };

  if (!currentReport) {
    return (
      <div className="min-h-screen bg-[#0A0B0F] text-white flex items-center justify-center">
        <RefreshCw className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  const hasRuntimeFilters =
    currentReport.filters?.some((f) => f.isRuntime) || false;
  const hasData = reportResults?.data && reportResults.data.length > 0;

  return (
    <div className="min-h-screen bg-[#0A0B0F] text-white p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/reports")}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold">{currentReport.name}</h1>
            {currentReport.description && (
              <p className="text-sm text-gray-400 mt-1">
                {currentReport.description}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          {hasRuntimeFilters && (
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Filter size={16} />
              <span>Filters</span>
            </button>
          )}

          <button
            onClick={handleRun}
            disabled={running}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition-all disabled:opacity-50"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
            }}
          >
            <Play size={16} className={running ? "animate-pulse" : ""} />
            <span>{running ? "Running..." : "Run Report"}</span>
          </button>

          <div className="relative group">
            <button className="flex items-center gap-2 px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors">
              <Download size={16} />
              <span>Export</span>
            </button>

            <div className="absolute right-0 top-12 w-48 bg-[#13161B] border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
              <button
                onClick={() => handleExport("csv")}
                disabled={exporting || !hasData}
                className="w-full px-4 py-2 text-left hover:bg-gray-800 rounded-t-lg disabled:opacity-50"
              >
                Export as CSV
              </button>
              <button
                onClick={() => handleExport("xlsx")}
                disabled={exporting || !hasData}
                className="w-full px-4 py-2 text-left hover:bg-gray-800 disabled:opacity-50"
              >
                Export as Excel
              </button>
              <button
                onClick={() => handleExport("pdf")}
                disabled={exporting || !hasData}
                className="w-full px-4 py-2 text-left hover:bg-gray-800 rounded-b-lg disabled:opacity-50"
              >
                Export as PDF
              </button>
            </div>
          </div>

          <div className="relative group">
            <button className="p-2 bg-[#0C0E12] border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors">
              <Edit size={16} />
            </button>

            <div className="absolute right-0 top-12 w-48 bg-[#13161B] border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
              <button
                onClick={() => navigate(`/reports/${id}/edit`)}
                className="w-full px-4 py-2 text-left hover:bg-gray-800 flex items-center gap-2 rounded-t-lg"
              >
                <Edit size={14} />
                Edit Report
              </button>
              <button
                onClick={handleClone}
                className="w-full px-4 py-2 text-left hover:bg-gray-800 flex items-center gap-2"
              >
                <Copy size={14} />
                Clone Report
              </button>
              <div className="border-t border-gray-700" />
              <button
                onClick={handleDelete}
                className="w-full px-4 py-2 text-left hover:bg-gray-800 flex items-center gap-2 text-red-400 rounded-b-lg"
              >
                <Trash2 size={14} />
                Delete Report
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Runtime Filters Panel */}
      {showFilters && hasRuntimeFilters && (
        <div className="bg-[#0C0E12] rounded-lg border border-gray-700 p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Runtime Filters</h3>
            <div className="flex gap-2">
              <button
                onClick={clearRuntimeFilters}
                className="text-sm text-blue-400 hover:text-blue-300"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowFilters(false)}
                className="p-1 hover:bg-gray-800 rounded transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentReport.filters
              ?.filter((f) => f.isRuntime)
              .map((filter, index) => (
                <div key={index}>
                  <label className="block text-sm text-gray-400 mb-2">
                    {filter.label || filter.field}
                  </label>
                  <input
                    type="text"
                    value={runtimeFilters[filter.field] || ""}
                    onChange={(e) =>
                      updateRuntimeFilter(filter.field, e.target.value)
                    }
                    placeholder={`Enter ${filter.label || filter.field}...`}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Report Info */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#0C0E12] rounded-lg border border-gray-700 p-4">
          <div className="text-sm text-gray-400 mb-1">Report Type</div>
          <div className="font-semibold">{currentReport.reportType}</div>
        </div>

        <div className="bg-[#0C0E12] rounded-lg border border-gray-700 p-4">
          <div className="text-sm text-gray-400 mb-1">Format</div>
          <div className="font-semibold capitalize">
            {currentReport.reportFormat}
          </div>
        </div>

        <div className="bg-[#0C0E12] rounded-lg border border-gray-700 p-4">
          <div className="text-sm text-gray-400 mb-1">Last Run</div>
          <div className="font-semibold">
            {currentReport.lastRunAt
              ? new Date(currentReport.lastRunAt).toLocaleString()
              : "Never"}
          </div>
        </div>

        <div className="bg-[#0C0E12] rounded-lg border border-gray-700 p-4">
          <div className="text-sm text-gray-400 mb-1">Total Runs</div>
          <div className="font-semibold">{currentReport.runCount || 0}</div>
        </div>
      </div>

      {/* Results Section */}
      {reportResults && (
        <div className="bg-[#0C0E12] rounded-lg border border-gray-700">
          {/* View Mode Toggle */}
          {currentReport.chartType !== "none" && hasData && (
            <div className="border-b border-gray-700 p-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode("table")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    viewMode === "table"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                  }`}
                >
                  <TableIcon size={16} />
                  Table
                </button>
                <button
                  onClick={() => setViewMode("chart")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    viewMode === "chart"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                  }`}
                >
                  <BarChart3 size={16} />
                  Chart
                </button>
              </div>
            </div>
          )}

          {/* Results Display */}
          <div className="p-6">
            {!hasData ? (
              <div className="text-center py-12">
                <BarChart3 size={48} className="mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-semibold mb-2">No Results Yet</h3>
                <p className="text-gray-400 mb-4">
                  Click "Run Report" to generate results
                </p>
              </div>
            ) : (
              <>
                {/* Summary Stats */}
                <div className="mb-6">
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                    <div>
                      <span className="font-semibold text-white">
                        {reportResults.data.length}
                      </span>{" "}
                      {reportResults.data.length === 1 ? "record" : "records"}
                    </div>
                    {reportResults.metadata?.executionTime && (
                      <div>
                        Execution time:{" "}
                        <span className="font-semibold text-white">
                          {reportResults.metadata.executionTime}ms
                        </span>
                      </div>
                    )}
                    {reportResults.metadata?.generatedAt && (
                      <div>
                        Generated:{" "}
                        <span className="font-semibold text-white">
                          {new Date(
                            reportResults.metadata.generatedAt,
                          ).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Chart View */}
                {viewMode === "chart" && currentReport.chartType !== "none" && (
                  <ReportChart
                    data={reportResults.data}
                    chartType={currentReport.chartType}
                    chartConfig={currentReport.chartConfig}
                    columns={currentReport.columns}
                    groupings={currentReport.groupings}
                    aggregates={currentReport.aggregates}
                  />
                )}

                {/* Table View */}
                {viewMode === "table" && (
                  <ReportTable
                    data={reportResults.data}
                    columns={currentReport.columns}
                    groupings={currentReport.groupings}
                    aggregates={currentReport.aggregates}
                    reportFormat={currentReport.reportFormat}
                    expandedGroups={expandedGroups}
                    onToggleGroup={toggleGroup}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Empty State - No Results Run Yet */}
      {!reportResults && (
        <div className="bg-[#0C0E12] rounded-lg border border-gray-700 p-12 text-center">
          <Play size={48} className="mx-auto mb-4 text-gray-600" />
          <h3 className="text-lg font-semibold mb-2">
            Ready to Run Your Report
          </h3>
          <p className="text-gray-400 mb-6">
            Click the "Run Report" button to generate results based on your
            configuration
          </p>
          <button
            onClick={handleRun}
            disabled={running}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {running ? "Running..." : "Run Report Now"}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportView;
